import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useState } from "react";
import {
  DEFAULT_FILTERS,
  filterMachines,
  type MachineFilterState,
  MachineFilters,
} from "@/components/machine-filters";
import { MachineTable } from "@/components/machine-table";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { useMachines } from "@/hooks/use-machines";
import { usePageTitle } from "@/hooks/use-page-title";
import { api } from "@/lib/api";

export function AllMachinesPage() {
  usePageTitle("Machines");
  const { user } = useAuth();
  const { data: machines = [], isLoading } = useMachines();
  const [filters, setFilters] = useState<MachineFilterState>(DEFAULT_FILTERS);
  const queryClient = useQueryClient();

  const syncInventory = useMutation({
    mutationFn: () => api.triggerJob("inventory_sync"),
    onSuccess: () => {
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: ["machines"] }), 3000);
    },
  });

  const currentUserId = user?.id ?? 0;
  const filtered = filterMachines(machines, filters, currentUserId);

  // Shared between the loading state and the table's sticky header slot.
  const filtersBar = (
    <MachineFilters
      filters={filters}
      onChange={setFilters}
      machines={machines}
      currentUserId={currentUserId}
    />
  );

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4">
        <h1 className="text-[15px] font-semibold text-foreground">All Machines</h1>
        <div className="flex items-center gap-2">
          {user?.is_admin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => syncInventory.mutate()}
              disabled={syncInventory.isPending}
              className="h-7 gap-1.5 text-[12px] text-muted-foreground"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${syncInventory.isPending ? "animate-spin" : ""}`}
              />
              Sync
            </Button>
          )}
        </div>
      </div>
      {isLoading ? (
        <>
          {filtersBar}
          <div className="flex items-center justify-center py-20">
            <span className="text-sm text-muted-foreground">Loading machines...</span>
          </div>
        </>
      ) : (
        <MachineTable
          machines={filtered}
          currentUserId={currentUserId}
          isAdmin={user?.is_admin}
          stickyHeader={filtersBar}
        />
      )}
    </div>
  );
}
